import { configurePipelineClient } from "./pipeline";
import { configureDb, getDocClient, putItem, scanAll, getItem, updateItem, Tables } from "./db";
import { configureCodeCommit } from "./commits";
import type { AuthSession, PortalRole } from "../types";

const USERS_TABLE = Tables.USERS;
const STORAGE_KEY = "devportal-auth-session";

/**
 * Bootstrap credentials — baked into the build via env vars.
 * This user can ONLY read the pos-portal-users table.
 * Used to authenticate logins from any device.
 */
const BOOTSTRAP_CREDENTIALS = {
  accessKeyId: import.meta.env.VITE_BOOTSTRAP_ACCESS_KEY_ID || "",
  secretAccessKey: import.meta.env.VITE_BOOTSTRAP_SECRET_ACCESS_KEY || "",
};

let bootstrapped = false;

/** Ensure DB is configured with at least bootstrap credentials */
function ensureBootstrapped() {
  if (!bootstrapped && BOOTSTRAP_CREDENTIALS.accessKeyId) {
    configureDb(BOOTSTRAP_CREDENTIALS);
    bootstrapped = true;
  }
}

export function isBootstrapped(): boolean {
  return bootstrapped || !!BOOTSTRAP_CREDENTIALS.accessKeyId;
}

/**
 * Manual bootstrap — for first-time setup when env vars aren't set.
 */
export function bootstrap(credentials: {
  accessKeyId: string;
  secretAccessKey: string;
}) {
  configureDb(credentials);
  bootstrapped = true;
}

export interface PortalUser {
  username: string;
  passwordHash: string;
  role: PortalRole;
  displayName: string;
  accessKeyId: string;
  secretAccessKey: string;
  createdAt: string;
  lastLogin?: string;
}

/**
 * Simple hash for passwords (browser-compatible).
 * In production you'd use bcrypt via a backend Lambda,
 * but for a Tooling portal this is adequate.
 */
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Login with username and password.
 * Looks up user in DynamoDB, verifies password, configures SDK clients
 * with the user's stored IAM credentials.
 */
export async function login(username: string, password: string): Promise<AuthSession> {
  // Ensure we can read the users table
  ensureBootstrapped();

  // Look up user
  const user = await getItem(USERS_TABLE, { username }) as PortalUser | undefined;
  if (!user) {
    throw new Error("Invalid username or password");
  }

  // Verify password
  const hash = await hashPassword(password);
  if (hash !== user.passwordHash) {
    throw new Error("Invalid username or password");
  }

  // Configure SDK clients with this user's IAM credentials
  const credentials = {
    accessKeyId: user.accessKeyId,
    secretAccessKey: user.secretAccessKey,
  };

  configurePipelineClient(credentials);
  configureDb(credentials);
  configureCodeCommit(credentials);

  // Update last login
  try {
    await updateItem(USERS_TABLE, { username }, { lastLogin: new Date().toISOString() });
  } catch {
    // non-critical
  }

  // Build session
  const session: AuthSession = {
    username: user.username,
    displayName: user.displayName,
    role: user.role,
    accessKeyId: user.accessKeyId,
    secretAccessKey: user.secretAccessKey,
  };

  // Persist session
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

  return session;
}

/** Restore session from localStorage */
export function restoreSession(): AuthSession | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const session: AuthSession = JSON.parse(stored);

    // Reconfigure SDK clients
    const credentials = {
      accessKeyId: session.accessKeyId,
      secretAccessKey: session.secretAccessKey,
    };
    configurePipelineClient(credentials);
    configureDb(credentials);
    configureCodeCommit(credentials);
    bootstrapped = true;

    return session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/** Clear session */
export function logout(): void {
  localStorage.removeItem(STORAGE_KEY);
  bootstrapped = false;
}

// ============================
// User Management (Admin only)
// ============================

/** Create a new portal user */
export async function createUser(data: {
  username: string;
  password: string;
  role: PortalRole;
  displayName: string;
  accessKeyId: string;
  secretAccessKey: string;
}): Promise<PortalUser> {
  ensureBootstrapped();

  const existing = await getItem(USERS_TABLE, { username: data.username });
  if (existing) {
    throw new Error(`User "${data.username}" already exists`);
  }

  const user: PortalUser = {
    username: data.username,
    passwordHash: await hashPassword(data.password),
    role: data.role,
    displayName: data.displayName,
    accessKeyId: data.accessKeyId,
    secretAccessKey: data.secretAccessKey,
    createdAt: new Date().toISOString(),
  };

  await putItem(USERS_TABLE, user);
  return user;
}

/** Get all portal users (admin view) */
export async function getAllUsers(): Promise<Omit<PortalUser, "passwordHash" | "secretAccessKey">[]> {
  const items = await scanAll(USERS_TABLE);
  return (items as PortalUser[]).map(({ passwordHash, secretAccessKey, ...rest }) => rest);
}

/** Update a user's role */
export async function updateUserRole(username: string, role: PortalRole): Promise<void> {
  await updateItem(USERS_TABLE, { username }, { role });
}

/** Update a user's password */
export async function updateUserPassword(username: string, newPassword: string): Promise<void> {
  const hash = await hashPassword(newPassword);
  await updateItem(USERS_TABLE, { username }, { passwordHash: hash });
}

/** Update a user's IAM credentials */
export async function updateUserCredentials(
  username: string,
  accessKeyId: string,
  secretAccessKey: string
): Promise<void> {
  await updateItem(USERS_TABLE, { username }, { accessKeyId, secretAccessKey });
}

/** Delete a portal user */
export async function deleteUser(username: string): Promise<void> {
  const { DeleteCommand } = await import("@aws-sdk/lib-dynamodb");
  await getDocClient().send(
    new DeleteCommand({ TableName: USERS_TABLE, Key: { username } })
  );
}

// ============================
// Permissions
// ============================

export type Permission =
  | "pipeline:view"
  | "pipeline:trigger"
  | "pipeline:stop"
  | "pipeline:approve-frontend"
  | "pipeline:approve-backend"
  | "pipeline:rollback"
  | "pipeline:gates"
  | "versions:create"
  | "versions:promote-dev"
  | "versions:promote-beta"
  | "versions:promote-prod"
  | "features:manage"
  | "bugs:manage"
  | "admin:users";

const ROLE_PERMISSIONS: Record<PortalRole, Permission[]> = {
  admin: [
    "pipeline:view",
    "pipeline:trigger",
    "pipeline:stop",
    "pipeline:approve-frontend",
    "pipeline:approve-backend",
    "pipeline:rollback",
    "pipeline:gates",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "versions:promote-prod",
    "features:manage",
    "bugs:manage",
    "admin:users",
  ],
  authorizer: [
    "pipeline:view",
    "pipeline:approve-frontend",
    "pipeline:approve-backend",
    "pipeline:rollback",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "versions:promote-prod",
    "features:manage",
    "bugs:manage",
  ],
  user: [
    "pipeline:view",
    "pipeline:approve-frontend",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "features:manage",
    "bugs:manage",
  ],
};

/** Check if a role has a specific permission */
export function hasPermission(role: PortalRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Get all permissions for a role */
export function getPermissions(role: PortalRole): Permission[] {
  return ROLE_PERMISSIONS[role];
}
